import { Divider, Dropdown, MessageBar, MessageBarBody, Option, Switch, Tag } from '@fluentui/react-components';
import { Delete20Regular, Edit20Regular } from '@fluentui/react-icons';
import { Button } from '../../Button';
import { Flex } from '../../Flex';
import { Loader } from '../../Loader';
import { Text } from '../../Text';
import {
  Day,
  generateDateFromTime,
  TimePattern,
  TimePatternSource,
  TimeSlot,
} from '@mnspro-cloud/services';
import cn from 'classnames';
import { isEqual, sortBy } from 'lodash';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ThemeMode, useProviderTheme } from '../../hooks';
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog';
import style from './TimepatternSettings.module.css';
import { DayWithMappings } from './TimepatternSettingsComponents/DayWithMappings/DayWithMappings';

export interface TimepatternSettingsProps {
  onSave: (days: Day[], slots: TimeSlot[], breakSlots: TimeSlot[], isActive: boolean) => void;
  onDelete?: () => void;
  onCancel?: () => void;
  onActiveChanged?: (active: boolean) => void;
  createMode?: boolean;
  isSaving: boolean;
  timepattern: TimePattern;
}

export const TimepatternSettings: React.FC<TimepatternSettingsProps> = (props) => {
  const { t } = useTranslation();
  const { mode } = useProviderTheme();
  const [mappings, setMappings] = useState<TimeSlot[]>(
    sortBy([...props.timepattern.slots, ...(props.timepattern.breakSlots ?? [])], ['start']),
  );
  const [selectedDays, setSelectedDays] = useState<Day[]>(props.timepattern.weekdays);

  const [isActive, setIsActive] = useState<boolean>(props.timepattern.isActive ?? true);
  //set if its components are in creation mode!!
  const [isInCreationMode, setIsInCreationMode] = useState<boolean>(false);
  const [daysErrors, setDaysErrors] = useState<string[]>([]);
  const [hourRowsErrors, setHourRowsErrors] = useState<string[]>([]);
  const [componentsValid, setComponentsValid] = useState<boolean>(true);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isInEditMode, setIsInEditMode] = useState<boolean>(props.createMode ?? false);

  const { getConfirmation } = useConfirmationDialog();

  const originalMappings = sortBy(
    [...props.timepattern.slots, ...(props.timepattern.breakSlots ?? [])],
    ['start'],
  );
  const hasChanges =
    !isEqual(originalMappings, mappings) ||
    !isEqual(props.timepattern.weekdays, selectedDays) ||
    !props.timepattern.isActive === isActive;

  const sequenceIsValid = (): boolean => {
    let isValid = true;
    for (let index = 1; index < mappings.length; index++) {
      const current = mappings[index];
      const previous = mappings[index - 1];
      if (moment(generateDateFromTime(previous.end)).isAfter(generateDateFromTime(current.start))) {
        isValid = false;
        break;
      }
    }
    return isValid;
  };

  const handleValidityInComponentChanged = (componentIsValid: boolean) => {
    setComponentsValid(componentIsValid);
  };

  const validateForm = () => {
    const noSelectedDaysErrors = [];
    const patternOrderErrors = [];
    if (selectedDays.length === 0) {
      noSelectedDaysErrors.push(
        t('no_days_selected', { defaultValue: 'Noch keine Tage ausgewählt.' }),
      );
    }
    if (mappings.length === 0) {
      patternOrderErrors.push(
        t('no_patterns_given', { defaultValue: 'Es müssen Schulstunden angegeben werden.' }),
      );
    }
    if (!sequenceIsValid()) {
      patternOrderErrors.push(
        t('pattern_order_not_correct', {
          defaultValue:
            'Die Reihenfolge der angelegten Stunden ist fehlerhaft. Bitte überprüfen Sie die Uhrzeiten.',
        }),
      );
    }
    setDaysErrors(noSelectedDaysErrors);
    setHourRowsErrors(patternOrderErrors);
    setIsValid(
      noSelectedDaysErrors.length === 0 && patternOrderErrors.length === 0 && componentsValid,
    );
  };

  useEffect(() => {
    validateForm();
  }, [mappings, selectedDays, componentsValid, isActive]);

  const allDays = [
    { item: Day.monday, header: t(Day.monday, { defaultValue: 'Montag' }) },
    { item: Day.tuesday, header: t(Day.tuesday, { defaultValue: 'Dienstag' }) },
    { item: Day.wednesday, header: t(Day.wednesday, { defaultValue: 'Mittwoch' }) },
    { item: Day.thursday, header: t(Day.thursday, { defaultValue: 'Donnerstag' }) },
    { item: Day.friday, header: t(Day.friday, { defaultValue: 'Freitag' }) },
    { item: Day.saturday, header: t(Day.saturday, { defaultValue: 'Samstag' }) },
    { item: Day.sunday, header: t(Day.sunday, { defaultValue: 'Sonntag' }) },
  ];

  const handleAddItem = (hourRow: TimeSlot) => {
    const newSlot: TimeSlot = {
      ...hourRow,
      period: 0, // Will be recalculated when saving
    };
    setMappings(sortBy([...mappings, newSlot], ['start']));
  };

  const handleDeleteItem = (itemToDelete: TimeSlot) => {
    const indexToRemove = mappings.findIndex((mapping) => isEqual(mapping, itemToDelete));
    const mappingsClone = [...mappings];
    mappingsClone.splice(indexToRemove, 1);
    setMappings([...mappingsClone]);
  };

  const handleUpdateMapping = (updatedItem: TimeSlot, index: number) => {
    const copy = [...mappings];
    copy[index] = {
      ...copy[index],
      ...updatedItem,
    };
    setMappings(sortBy(copy, ['start']));
  };

  const handleSelectedDayChange = (_: React.SyntheticEvent, data: { selectedOptions: string[] }) => {
    setSelectedDays(data.selectedOptions as Day[]);
  };

  const handleButtonSaveClick = () => {
    const slots = mappings.filter((m) => !m.isBreak);
    const breakSlots = mappings.filter((m) => m.isBreak);
    props.onSave(selectedDays, slots, breakSlots, isActive);
    setIsInEditMode(false);
  };

  const handleCancelButtonClick = () => {
    setSelectedDays(props.timepattern.weekdays);
    setMappings(
      sortBy([...props.timepattern.slots, ...(props.timepattern.breakSlots ?? [])], ['start']),
    );
    setIsInEditMode(false);
    props.onCancel?.();
  };

  const handleIsActiveCheckboxChanged = async (checked: boolean) => {
    const shouldChange = await getConfirmation({
      headerText: t(
        'settings:classregister:timepattern:change_activation_state_confirmation_header',
        { defaultValue: 'Stundenplanraster ändern' },
      ),
      content: t(
        'settings:classregister:timepattern:change_activation_state_confirmation_content',
        {
          defaultValue:
            'Sind Sie sicher dass Sie den Aktivierungsstatus des Stundenplanrasters ändern möchten?',
        },
      ),
      cancelButtonText: t('cancel', { defaultValue: 'Abbrechen' }),
      confirmButtonText: t('change', { defaultValue: 'Ändern' }),
      showCloseButton: true,
    });

    if (shouldChange) {
      props.onActiveChanged?.(checked);
    }
  };

  const handleCreationModeChanged = (isInCreationMode: boolean) =>
    setIsInCreationMode(isInCreationMode);

  const handleEditButtonClicked = () => setIsInEditMode(true);

  const deleteButtonClicked = () => {
    props.onDelete?.();
  };

  // i18next-cli: t('Monday', { defaultValue: 'Montag' })
  // i18next-cli: t('Tuesday', { defaultValue: 'Dienstag' })
  // i18next-cli: t('Wednesday', { defaultValue: 'Mittwoch' })
  // i18next-cli: t('Thursday', { defaultValue: 'Donnerstag' })
  // i18next-cli: t('Friday', { defaultValue: 'Freitag' })
  // i18next-cli: t('Saturday', { defaultValue: 'Samstag' })
  // i18next-cli: t('Sunday', { defaultValue: 'Sonntag' })
  const dayComponent = isInEditMode ? (
    <Dropdown
      multiselect
      data-testid="patternDaysSelector"
      placeholder={t('choose_days', { defaultValue: 'Bitte Tag(e) auswählen' })}
      // Use Dropdown (not Combobox) so the field is select-only: no editable text
      // input / caret. The displayed text is controlled via `value`, since the
      // Fluent v9 multiselect Dropdown does not render the selection automatically.
      value={allDays
        .filter((day) => selectedDays.includes(day.item))
        .map((day) => day.header)
        .join(', ')}
      selectedOptions={selectedDays}
      onOptionSelect={handleSelectedDayChange}
    >
      {allDays.map((day) => (
        <Option key={day.item} value={day.item} text={day.header}>
          {day.header}
        </Option>
      ))}
    </Dropdown>
  ) : (
    <Flex inline gap="0.5rem" wrap>
      {selectedDays.map((day, index) => (
        <Tag appearance="outline" size="extra-small" key={index} className={style.pill}>
          {t(day)}
        </Tag>
      ))}
    </Flex>
  );

  const activeCheckbox = (
    <Flex align="center">
      <Switch
        checked={isActive}
        onChange={(_, data) => handleIsActiveCheckboxChanged(data.checked)}
        data-testid="activeToggle"
        disabled={props.isSaving || isInEditMode}
      />
      {isActive ? (
        <Text color="success" weight="bold">{t('is_active', { defaultValue: 'Aktiv' })}</Text>
      ) : (
        <Text color="danger">{t('not_active', { defaultValue: 'Inaktiv' })}</Text>
      )}
    </Flex>
  );

  return (
    <Flex
      direction="column"
      gap="0.5rem"
      className={cn(style.timepatternSettings, {
        [style.dark]: mode === ThemeMode.Dark,
      })}
    >
      <Flex inline>
        {activeCheckbox}
        <Flex gap="0.5rem" fill justify="flex-end">
          <Button
            icon={<Edit20Regular />}
            appearance="transparent"
            disabled={props.timepattern.source === TimePatternSource.External || isInEditMode}
            onClick={handleEditButtonClicked}
            aria-label={t('settings:classbook:timepattern:edit_a11y', { defaultValue: 'Stundenplanraster bearbeiten' })}
          />
          <Button
            icon={<Delete20Regular />}
            appearance="transparent"
            disabled={props.timepattern.source === TimePatternSource.External || isInEditMode}
            onClick={deleteButtonClicked}
            aria-label={t('settings:classbook:timepattern:delete_a11y', { defaultValue: 'Stundenplanraster löschen' })}
          />
        </Flex>
      </Flex>
      {props.timepattern.source === TimePatternSource.External && (
        <MessageBar intent="warning">
          <MessageBarBody>
            {t('settings:classbook:course:external_timepattern_alert', {
              defaultValue:
                'Die Zeiten wurden aus externer Quelle importiert und können nicht verändert werden.',
            })}
          </MessageBarBody>
        </MessageBar>
      )}
      <Text>{t('settings_timepattern_days_label', { defaultValue: 'Schultage' }) + ':'}</Text>
      {dayComponent}
      {daysErrors.map((error, index) => (
        <Text color="danger" key={index}>{error}</Text>
      ))}
      <Text>
        {t('settings_timepattern_hours_label', { defaultValue: 'Schulstunden' }) + ':'}
      </Text>
      <Flex gap="0.5rem">
        {hourRowsErrors.length > 0 && <Divider vertical className={style.ChromeHack} style={{ borderColor: 'var(--colorPaletteRedForeground1, red)' }} />}
        <Flex direction="column" gap="0.5rem" fill>
          <DayWithMappings
            mappings={mappings}
            originalMappings={originalMappings}
            addMapping={handleAddItem}
            deleteButtonClicked={handleDeleteItem}
            updateMapping={handleUpdateMapping}
            validityChanged={handleValidityInComponentChanged}
            creationModeChanged={handleCreationModeChanged}
            displayMode={!isInEditMode}
          />
          {hourRowsErrors.map((error, index) => (
            <Text color="danger" key={index}>{error}</Text>
          ))}
        </Flex>
      </Flex>
      {isInEditMode && (
        <Flex justify="flex-end" gap="0.5rem">
          <Button
            data-testid="patternCancelButton"
            onClick={handleCancelButtonClick}
            disabled={props.isSaving || isInCreationMode}
          >
            {t('cancel', { defaultValue: 'Abbrechen' })}
          </Button>
          <Button
            data-testid="patternSaveButton"
            appearance="primary"
            onClick={handleButtonSaveClick}
            icon={
              props.isSaving ? (
                <Loader data-testid="patternSaveButtonLoader" size="sm" label="" />
              ) : undefined
            }
            disabled={props.isSaving || !isValid || isInCreationMode || !hasChanges}
          >
            {t('save', { defaultValue: 'Speichern' })}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};
